import { useParams, Link } from 'react-router-dom';
import { PageSEO } from '@/components/base/PageSEO';
import { Breadcrumb, getCategoryName } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const allArticles = articles;
  const related = allArticles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 4);

  if (!article) {
    return (
      <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-search-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryName = getCategoryName(article.category);
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '一起草',
      url: 'https://jlsjhjt.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jlsjhjt.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        schema={[articleSchema]}
      />

      <main className="pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">&middot;</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-secondary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 一起草`}
                className="w-full h-auto max-h-[450px] object-cover"
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-50 text-secondary-700 rounded-lg text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-4">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 bg-background-100 rounded-lg flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 bg-background-100 rounded-lg flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 bg-background-100 rounded-lg flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link-m text-foreground-600"></i>
              </button>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-14">
              <h3 className="font-heading font-semibold text-xl text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/article/${r.slug}`}
                    className="flex gap-4 bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-background-300/60 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={r.image}
                        alt={r.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors mb-1.5">
                        {r.title}
                      </h4>
                      <p className="text-xs text-foreground-400">{r.date} &middot; {r.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .prose-custom h2 { font-family: var(--font-heading); font-size: 1.5rem; font-weight: 700; color: oklch(var(--foreground-900)); margin-top: 2rem; margin-bottom: 1rem; }
        .prose-custom h3 { font-family: var(--font-heading); font-size: 1.25rem; font-weight: 600; color: oklch(var(--foreground-800)); margin-top: 1.75rem; margin-bottom: 0.75rem; }
        .prose-custom p { font-size: 0.9375rem; line-height: 1.85; color: oklch(var(--foreground-700)); margin-bottom: 1.25rem; }
        .prose-custom ul { padding-left: 1.5rem; margin-bottom: 1.25rem; }
        .prose-custom li { font-size: 0.9375rem; line-height: 1.85; color: oklch(var(--foreground-700)); margin-bottom: 0.5rem; list-style-type: disc; }
        .prose-custom strong { color: oklch(var(--foreground-900)); font-weight: 600; }
      `}</style>
    </>
  );
}